import * as signalR from '@microsoft/signalr';
import { JWT_HOST_API } from 'configs/auth.config';

/**
 * SignalR Notification Service
 * Manages real-time notification connections with the backend
 */
class SignalRNotificationService {
  constructor() {
    this.connection = null;
    this.listeners = new Map();
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * Initialize and start SignalR connection
   * @param {string} accessToken - JWT token for authentication
   * @returns {Promise<boolean>} - Success status
   */
  async startConnection(accessToken) {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.warn('⚠️ SignalR connection already in progress');
      return false;
    }

    // If already connected, return success
    if (this.isConnected()) {
      // console.log('✅ SignalR already connected');
      return true;
    }

    if (!accessToken) {
      console.warn('⚠️ Cannot start connection: Missing access token');
      return false;
    }

    try {
      this.isConnecting = true;

      // Build the hub URL
      const hubUrl = `${JWT_HOST_API}hubs/notification`;
      // console.log('🔗 Connecting to SignalR Hub:', hubUrl);

      // Build the connection with authentication
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => accessToken,
          skipNegotiation: false,
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0s, 2s, 10s, 30s, then 60s
            if (retryContext.elapsedMilliseconds < 60000) {
              return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
            }
            return 60000; // Max 60 seconds between retries
          }
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Set up connection lifecycle handlers
      this.setupConnectionHandlers();

      // Set up event listeners
      this.setupEventListeners();

      // Start the connection
      await this.connection.start();
      // console.log('✅ SignalR Connected Successfully');
      // console.log('📍 Connection ID:', this.connection.connectionId);

      this.reconnectAttempts = 0;
      this.isConnecting = false;

      return true;
    } catch (error) {
      this.isConnecting = false;
      console.error('❌ SignalR Connection Error:', error);

      const isUnauthorized = 
        error?.statusCode === 401 || 
        (error?.message && (
          error.message.includes('401') || 
          error.message.toLowerCase().includes('unauthorized')
        ));

      if (isUnauthorized) {
        console.warn('🔑 SignalR connection failed with 401 Unauthorized. Purging stale auth token.');
        localStorage.removeItem('authToken');
        if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/login') {
          window.location.href = '/login';
        }
      }

      // Notify listeners of connection error
      this.notifyListeners('error', {
        message: 'Failed to connect to notification service',
        error
      });

      throw error;
    }
  }

  /**
   * Setup connection lifecycle handlers
   */
  setupConnectionHandlers() {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      console.warn('⚠️ SignalR Reconnecting...', error);
      this.notifyListeners('reconnecting', { error });
    });

    this.connection.onreconnected((connectionId) => {
      // console.log('✅ SignalR Reconnected:', connectionId);
      this.reconnectAttempts = 0;

      // Re-register event listeners after reconnection
      this.setupEventListeners();

      this.notifyListeners('reconnected', { connectionId });
    });

    this.connection.onclose((error) => {
      console.error('❌ SignalR Connection Closed:', error);
      this.notifyListeners('closed', { error });

      // Attempt to reconnect if it wasn't a graceful disconnect
      if (error && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        // console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

        setTimeout(() => {
          const token = localStorage.getItem('authToken');
          if (token) {
            this.startConnection(token).catch(err => {
              console.error('Reconnection failed:', err);
            });
          }
        }, 2000 * this.reconnectAttempts);
      }
    });
  }

  /**
   * Setup event listeners for SignalR hub events
   */
  setupEventListeners() {
    if (!this.connection) return;

    // console.log('📡 Setting up SignalR event listeners...');

    // Set up listener for notifications
    this.connection.on('ReceiveNotification', (message) => {
      this.handleNotification(message);
    });

    // Set up listener for the authoritative unread count pushed by the server
    // after every new notification (NotificationService.cs -> AddNotification)
    this.connection.on('ReceiveNotificationCount', (count) => {
      this.notifyListeners('notificationCount', { count });
    });

    // Set up listener for role permission updates
    this.connection.on('UPDATE_ROLE_PERMISSION', (data) => {
      this.handleRolePermissionUpdate(data);
    });

    // Set up listener for inbound call popup
    this.connection.on('ReceiveInboundCallPopup', (data) => {
      this.notifyListeners('ReceiveInboundCallPopup', data);
    });

    // Set up listener for WHS auto-checkout (worker checked in elsewhere, closing this job's session)
    this.connection.on('WHS_ForceCheckOut', (data) => {
      this.notifyListeners('WHS_ForceCheckOut', data);
    });

    // console.log('✅ SignalR event listeners registered');
  }

  /**
   * Handle role permission update notification
   * @param {object} data - Role permission update data
   */
  handleRolePermissionUpdate(data) {
    try {
      // console.log('🔐 Role Permission Update Received');

      // 1. Extract current user role from auth token
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;
      const payload = authToken.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      const currentRoleId = decoded?.roleId || decoded?.RoleId;

      // 2. If roleId does not match, ignore this event
      if (!currentRoleId || currentRoleId !== data) {
        // console.log("🔕 Role update ignored for unmatched roleId");
        return;
      }

      // 3. Prepare structured notification
      const roleUpdateNotification = {
        type: 'ROLE_PERMISSION_UPDATE',
        message: 'Permissions for your role have been changed',
        roleId: currentRoleId,
        timestamp: new Date().toISOString(),
        requiresLogout: true,
        countdownSeconds: 10
      };

      // 4. Notify relevant listeners
      this.notifyListeners('UPDATE_ROLE_PERMISSION', roleUpdateNotification);
      this.notifyListeners('notification', roleUpdateNotification);

    } catch (error) {
      console.error('Error handling role permission update:', error);
      this.notifyListeners('error', {
        message: 'Error processing role permission update',
        error
      });
    }
  }


  /**
   * Handle incoming notification
   * @param {string|object} message - Notification message (JSON string or object)
   */
  handleNotification(message) {
    try {
      // Try to parse as JSON if it's a string
      let notification = message;
      if (typeof message === 'string') {
        try {
          notification = JSON.parse(message);
        } catch (parseError) {
          // If parsing fails, treat as plain text
          notification = { message };
        }
      }

      // If it has EventTypeCode and Payload, render it
      if (notification.EventTypeCode && notification.Payload) {
        import('utils/notificationTemplates').then(({ renderNotification }) => {
          const renderedDetails = renderNotification(notification.EventTypeCode, notification.Payload);
          notification = {
            ...notification,
            ...renderedDetails, // adds title, body, link, icon, color
          };

          // Notify all listeners
          this.notifyListeners('notification', notification);

          // Also emit a specific event with timestamp
          this.notifyListeners('notificationReceived', {
            ...notification,
            receivedAt: new Date().toISOString()
          });
        }).catch(err => {
          console.error('Failed to load templates:', err);
          this.notifyListeners('notification', notification);
        });
        return;
      }

      // Fallback for older format
      // console.log('📬 Notification Received:', notification);

      // Notify all listeners
      this.notifyListeners('notification', notification);

      // Also emit a specific event with timestamp
      this.notifyListeners('notificationReceived', {
        ...notification,
        receivedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error handling notification:', error);
      this.notifyListeners('error', { message: 'Error processing notification', error });
    }
  }

  /**
   * Subscribe to notification events
   * @param {string} eventType - Event type: 'notification', 'reconnecting', 'reconnected', 'closed', 'error'
   * @param {function} callback - Callback function to handle the event
   * @returns {string} - Subscription ID for unsubscribing
   */
  subscribe(eventType, callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    const subscriptionId = `${eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Map());
    }

    this.listeners.get(eventType).set(subscriptionId, callback);
    // console.log(`Subscribed to '${eventType}' (ID: ${subscriptionId})`);

    return subscriptionId;
  }

  /**
   * Unsubscribe from notifications
   * @param {string} subscriptionId - The subscription ID returned from subscribe()
   */
  unsubscribe(subscriptionId) {
    const [eventType] = subscriptionId.split('_');
    if (this.listeners.has(eventType)) {
      const removed = this.listeners.get(eventType).delete(subscriptionId);
      if (removed) {
        // console.log(`Unsubscribed from '${eventType}' (ID: ${subscriptionId})`);
      }
    }
  }

  /**
   * Notify all listeners of an event
   * @param {string} eventType - Event type
   * @param {any} data - Event data
   */
  notifyListeners(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach((callback, subscriptionId) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in notification listener (${subscriptionId}):`, error);
        }
      });
    }
  }

  /**
   * Stop the SignalR connection
   */
  async stopConnection() {
    if (this.connection) {
      try {
        await this.connection.stop();
        // console.log(' SignalR Disconnected');
        this.connection = null;
        this.listeners.clear();
        this.reconnectAttempts = 0;
      } catch (error) {
        console.error('Error stopping SignalR:', error);
      }
    }
  }

  /**
   * Get current connection state
   * @returns {string} - Connection state
   */
  getConnectionState() {
    if (!this.connection) return 'Disconnected';

    const state = this.connection.state;
    return state.charAt(0).toUpperCase() + state.slice(1);
  }

  /**
   * Check if connected
   * @returns {boolean} - True if connected
   */
  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Get connection ID
   * @returns {string|null} - Connection ID or null if not connected
   */
  getConnectionId() {
    return this.connection?.connectionId || null;
  }

  /**
   * Check connection health and reconnect if needed
   * @returns {Promise<boolean>} - Connection status
   */
  async checkConnectionHealth() {
    if (!this.connection) {
      console.warn('⚠️ SignalR connection not initialized');
      return false;
    }

    const state = this.connection.state;
    // console.log(` SignalR connection state: ${state}`);

    if (state === signalR.HubConnectionState.Connected) {
      return true;
    } else if (state === signalR.HubConnectionState.Disconnected) {
      // console.log('SignalR disconnected, attempting to reconnect...');
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          await this.startConnection(token);
          return true;
        }
      } catch (error) {
        console.error('❌ Failed to reconnect:', error);
      }
      return false;
    } else {
      // console.log(` SignalR is ${state}, waiting...`);
      return false;
    }
  }

  /**
   * Get detailed connection status for debugging
   * @returns {object} - Connection status details
   */
  getConnectionStatus() {
    return {
      state: this.getConnectionState(),
      isConnected: this.isConnected(),
      connectionId: this.getConnectionId(),
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      isConnecting: this.isConnecting,
      listenerCount: this.listeners.size
    };
  }

  /**
   * Send a test message (for debugging)
   * @param {string} methodName - Hub method name
   * @param {any} args - Arguments to send
   */
  async invoke(methodName, ...args) {
    if (!this.isConnected()) {
      throw new Error('Not connected to SignalR hub');
    }

    try {
      return await this.connection.invoke(methodName, ...args);
    } catch (error) {
      console.error(`Error invoking ${methodName}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const signalRNotificationService = new SignalRNotificationService();
export default signalRNotificationService;

